import { getContext, setContext } from 'svelte';

export interface LayerContext {
    layer: number;
}

/**
 * Not a service: the layer lives in the Svelte context and so belongs to a place in the component tree.
 * getContext()/setContext() only work while a component initialises — create the handler there.
 */
export class LayerHandler {
    private static readonly ContentKey = 'content-layer';
    private static readonly NavigationKey = 'navigation-layer';

    public getLayer(key: string): number {
        const SearchContext = getContext<(() => LayerContext | undefined) | undefined>(key);
        if (!SearchContext) {
            return 0;
        }

        return Math.max(0, SearchContext()?.layer ?? -1);
    }

    public setLayer(key: string, layer: number): void {
        const WriteContext: LayerContext = {
            layer: Math.max(0, Math.round(layer)),
        };

        setContext(key, () => WriteContext);
    }

    public getContentLayer(): number {
        return this.getLayer(LayerHandler.ContentKey);
    }

    public setContentLayer(layer: number): void {
        this.setLayer(LayerHandler.ContentKey, layer);
    }

    /** One layer deeper than the parent's, and that layer for every child. */
    public upContentLayer(): number {
        const NextContentLayer = this.getContentLayer() + 1;
        this.setContentLayer(NextContentLayer);
        return NextContentLayer;
    }

    public getNavigationLayer(): number {
        return this.getLayer(LayerHandler.NavigationKey);
    }

    public setNavigationLayer(layer: number): void {
        this.setLayer(LayerHandler.NavigationKey, layer);
    }

    public upNavigationLayer(): number {
        const NextNavigationLayer = this.getNavigationLayer() + 1;
        this.setNavigationLayer(NextNavigationLayer);
        return NextNavigationLayer;
    }
}
